import React, { useEffect } from 'react';
import {
  Box, Typography, Card, CardContent, Grid,
  Avatar, Stack, Divider, LinearProgress, styled, Button
} from '@mui/material';
import {
  Assignment, CheckCircle, PendingActions, People,
  TrendingUp, ErrorOutline, AccessTime, CalendarToday
} from '@mui/icons-material';
import { ToastContainer, toast } from 'react-toastify';

// Styled Components
const DashboardCard = styled(Card)(({ theme }) => ({
  borderRadius: '12px',
  boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
  transition: 'transform 0.3s, box-shadow 0.3s',
  '&:hover': {
    transform: 'translateY(-4px)',
    boxShadow: '0 8px 24px rgba(0,0,0,0.12)'
  }
}));

const ProgressBar = styled(LinearProgress)(({ theme, value }) => ({
  height: 8,
  borderRadius: 4,
  backgroundColor: theme.palette.grey[200],
  '& .MuiLinearProgress-bar': {
    borderRadius: 4,
    background: value > 70 ? '#4caf50' : value > 40 ? '#ff9800' : '#f44336'
  }
}));

const StatCard = ({ icon, title, value, change }) => {
  const isPositive = change >= 0;
  
  return (
    <DashboardCard>
      <CardContent sx={{ p: 3 }}>
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Avatar sx={{ 
            bgcolor: 'rgba(211, 47, 47, 0.1)', 
            color: '#d32f2f',
            width: 48,
            height: 48
          }}>
            {icon}
          </Avatar>
          <Box sx={{ textAlign: 'right' }}>
            <Typography variant="h5" fontWeight={600}>{value}</Typography>
            <Typography variant="body2" color="text.secondary">{title}</Typography>
            <Typography 
              variant="caption" 
              color={isPositive ? 'success.main' : 'error.main'}
              sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end' }}
            >
              {isPositive ? '↑' : '↓'} {Math.abs(change)}% 
              {isPositive ? ' from last month' : ' from last month'}
            </Typography>
          </Box>
        </Stack>
      </CardContent>
    </DashboardCard>
  );
};

const PriorityIndicator = ({ priority, count }) => {
  const colors = {
    High: '#f44336',
    Medium: '#ff9800',
    Low: '#4caf50'
  };
  
  return (
    <Box sx={{ mb: 2 }}>
      <Stack direction="row" justifyContent="space-between" sx={{ mb: 1 }}>
        <Typography variant="body2">{priority}</Typography>
        <Typography variant="body2" fontWeight={600}>{count}</Typography>
      </Stack>
      <ProgressBar variant="determinate" value={count * 10} />
    </Box>
  );
};

const RecentTicket = ({ title, assignee, status, date }) => {
  const statusIcons = {
    'Pending': <AccessTime color="warning" />,
    'In Progress': <CalendarToday color="info" />,
    'Completed': <CheckCircle color="success" />
  };
  
  return (
    <Box sx={{ py: 2 }}>
      <Stack direction="row" spacing={2} alignItems="center">
        <Avatar sx={{ 
          bgcolor: 'rgba(211, 47, 47, 0.1)', 
          color: '#d32f2f',
          width: 40,
          height: 40
        }}>
          {statusIcons[status]}
        </Avatar>
        <Box sx={{ flex: 1 }}>
          <Typography fontWeight={500}>{title}</Typography>
          <Typography variant="body2" color="text.secondary">{assignee}</Typography>
        </Box>
        <Typography variant="caption" color="text.secondary">{date}</Typography>
      </Stack>
    </Box>
  );
};

const Dashboard = () => {
  useEffect(() => {
    toast.success("Welcome back!")
  }, []);
  // Sample data
  const stats = [
    { icon: <Assignment />, title: 'Total Tickets', value: '142', change: 12 },
    { icon: <CheckCircle />, title: 'Resolved', value: '89', change: 8 },
    { icon: <PendingActions />, title: 'Pending', value: '32', change: -4 },
    { icon: <People />, title: 'Active Users', value: '24', change: 5 }
  ];
  
  const priorities = [
    { priority: 'High', count: 8 },
    { priority: 'Medium', count: 5 },
    { priority: 'Low', count: 3 }
  ];
  
  const recentTickets = [
    { title: 'Website Redesign', assignee: 'Mike Chen', status: 'In Progress', date: '2d ago' },
    { title: 'Marketing Assets', assignee: 'Jane Smith', status: 'Pending', date: '1d ago' },
    { title: 'API Integration', assignee: 'Dev Team', status: 'Completed', date: '5d ago' }
  ];

  return (
    <Box sx={{ p: 3 }}>
      <ToastContainer />
      <Typography variant="h4" fontWeight={700} sx={{ mb: 3 }}>
        Dashboard Overview
      </Typography>
      
      {/* <Grid container spacing={3} sx={{ mb: 4 }}>
        {stats.map((stat, index) => (
          <Grid item xs={12} sm={6} md={3} key={index}>
            <StatCard {...stat} />
          </Grid>
        ))}
      </Grid> */}
      
      {/* <Grid container spacing={3}>
        <Grid item xs={12} md={8}>
          <DashboardCard sx={{ mb: 3 }}>
            <CardContent>
              <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 3 }}>
                <Typography variant="h6" fontWeight={600}>Ticket Activity</Typography>
                <Button size="small" color="primary">View All</Button>
              </Stack>
              <Box sx={{ height: 300 }}>
                <Box sx={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  justifyContent: 'center', 
                  height: '100%',
                  bgcolor: 'rgba(211, 47, 47, 0.03)',
                  borderRadius: 2
                }}>
                  <Typography color="text.secondary">Ticket activity chart would appear here</Typography>
                </Box>
              </Box>
            </CardContent>
          </DashboardCard>
          
          <DashboardCard>
            <CardContent>
              <Typography variant="h6" fontWeight={600} sx={{ mb: 2 }}>Recent Tickets</Typography>
              <Divider sx={{ mb: 2 }} />
              {recentTickets.map((ticket, index) => (
                <React.Fragment key={index}>
                  <RecentTicket {...ticket} />
                  {index < recentTickets.length - 1 && <Divider />}
                </React.Fragment>
              ))}
            </CardContent>
          </DashboardCard>
        </Grid>
        
        <Grid item xs={12} md={4}>
          <DashboardCard sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="h6" fontWeight={600} sx={{ mb: 2 }}>Priority Distribution</Typography>
              {priorities.map((item, index) => (
                <PriorityIndicator key={index} {...item} />
              ))}
            </CardContent>
          </DashboardCard>
          
          <DashboardCard>
            <CardContent>
              <Typography variant="h6" fontWeight={600} sx={{ mb: 2 }}>Team Performance</Typography>
              <Stack spacing={2}>
                <Box>
                  <Stack direction="row" justifyContent="space-between" sx={{ mb: 0.5 }}>
                    <Typography variant="body2">Completion Rate</Typography>
                    <Typography variant="body2" fontWeight={600}>78%</Typography>
                  </Stack>
                  <ProgressBar variant="determinate" value={78} />
                </Box>
                <Box>
                  <Stack direction="row" justifyContent="space-between" sx={{ mb: 0.5 }}>
                    <Typography variant="body2">On Time</Typography>
                    <Typography variant="body2" fontWeight={600}>65%</Typography>
                  </Stack>
                  <ProgressBar variant="determinate" value={65} />
                </Box>
                <Box>
                  <Stack direction="row" justifyContent="space-between" sx={{ mb: 0.5 }}>
                    <Typography variant="body2">Avg. Resolution</Typography>
                    <Typography variant="body2" fontWeight={600}>3.2 days</Typography>
                  </Stack>
                  <ProgressBar variant="determinate" value={72} />
                </Box>
              </Stack>
            </CardContent>
          </DashboardCard>
        </Grid>
      </Grid> */}
    </Box>
  );
};

export default Dashboard;